import * as React from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import type { StockOpportunity } from '@/lib/api-client'

interface Props {
  opportunities: StockOpportunity[]
  mask: (skuId: number, name: string) => string
  topN?: number
}

/** Horizontal bars: cross-branch demand imbalance ratio per SKU (the headline visual). */
export function DivergenceChart({ opportunities, mask, topN = 10 }: Props) {
  const data = React.useMemo(() => {
    const finite = opportunities.map((o) => o.ratio).filter((r): r is number => r != null)
    const cap = (finite.length ? Math.max(...finite) : 6) + 2 // bar length for ∞
    return opportunities
      .slice(0, topN)
      .map((o) => ({
        sku: mask(o.skuId, o.sku),
        ratio: o.ratio ?? cap,
        label: o.ratio == null ? '∞' : `${o.ratio}×`,
        fast: o.fastBranch,
        slow: o.slowBranch,
      }))
      .sort((a, b) => b.ratio - a.ratio)
  }, [opportunities, mask, topN])

  if (!data.length) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-text-muted">
        No imbalance ≥3× in this window.
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-text-dark">Cross-branch demand imbalance</h3>
        <span className="text-[11px] text-text-muted">fast branch ÷ slow branch</span>
      </div>
      <div style={{ height: Math.max(220, data.length * 34) }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 40, bottom: 4, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}×`} />
            <YAxis type="category" dataKey="sku" tick={{ fontSize: 11 }} width={120} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              formatter={(_v: any, _n: any, p: any) => [`${p.payload.label}  (${p.payload.fast} vs ${p.payload.slow})`, 'Imbalance']}
            />
            <Bar dataKey="ratio" radius={[0, 6, 6, 0]}>
              {data.map((d, i) => (
                <Cell key={d.sku} fill={i === 0 ? '#f43f5e' : i < 3 ? '#fb923c' : '#2563eb'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
